import { entries, isEmpty, keyBy, map, sortBy } from "lodash"
import { useMemo } from "react"
import styled from "styled-components"

import { projectAtoms } from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import CopyToClipboard from "#blocks/CopyToClipboard"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useSomeDocs } from "#hooks/jotai/useSubscription"
import { useCanSeeIds } from "#hooks/newHooks/useHasPermission"
import { COLORS } from "#styles/colors"
import { formatSemester } from "#utils/semesterHelpers"

const SProjectItem = styled(AnimateInFlex)`
  width: 250px;
  height: 80px;
  padding: 8px;
  background-color: ${COLORS.GRAY1};
  border-radius: 6px;
  border-left: 3px solid ${COLORS.rooms.MAIN_ROOM_1};
  justify-content: space-between;
  /* cursor: pointer; */
  /* transition: all 0.2s ease; */

  /* &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  } */
`

const SCompletionBadge = styled.div<{ isCompleted: boolean }>`
  display: inline-block;
  width: fit-content;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  background-color: ${({ isCompleted }) =>
    isCompleted ? COLORS.success : COLORS.warning};
  color: white;
  margin-left: 6px;
  margin-right: 8px;
  margin-bottom: 4px;
`

type ProjectForDisplay = {
  id: Uuid
  name: string
  semester: string
  daysToComplete: number
  medium: string
  daysCompleted: number
  isCompleted: boolean
  completionFraction: string
}

const useProjectsData = (
  projects: Record<Uuid, number> | undefined
): ProjectForDisplay[] => {
  const projectIds = useMemo(() => {
    if (!projects) return []
    return Object.keys(projects)
  }, [projects])

  const projectsFromDb = useSomeDocs<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    projectIds
  )

  const projectsById = useMemo(() => {
    return keyBy(projectsFromDb, "id")
  }, [projectsFromDb])

  return useMemo(() => {
    if (!projects || !projectsFromDb.length) return []

    return map(entries(projects), ([projectId, daysCompleted]) => {
      const project = projectsById[projectId]

      if (!project) {
        return {
          id: projectId,
          name: "Unknown Project",
          semester: "Unknown Semester",
          daysToComplete: 1,
          medium: "Unknown",
          daysCompleted,
          isCompleted: false,
          completionFraction: `${daysCompleted}/?`,
        }
      }

      const daysToComplete = parseInt(project.daysToComplete) || 1
      const isCompleted = daysCompleted >= daysToComplete

      return {
        id: projectId,
        name: project.name,
        semester: project.semester,
        daysToComplete,
        medium: project.medium,
        daysCompleted,
        isCompleted,
        completionFraction: `${daysCompleted}/${daysToComplete}`,
      }
    })
      .sort((a, b) => a.semester.localeCompare(b.semester))
      .reverse()
  }, [projects, projectsFromDb, projectsById])
}

type ProjectItemProps = {
  project: ProjectForDisplay
}

const ProjectItem = ({ project }: ProjectItemProps) => {
  const canSeeIds = useCanSeeIds()

  return (
    <SProjectItem col animation={ANIMATION_BY_KEY.leftSm}>
      {/* Header with name and copy button */}
      <Flex
        style={{
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "10px",
          fontWeight: "500",
        }}
        gap={4}
      >
        <Flex style={{ minWidth: 0 }} grow>
          <TextOverrun text={project.name} />
        </Flex>
        {canSeeIds && (
          <CopyToClipboard data={project.id} compact label={project.id} />
        )}
      </Flex>

      {/* Medium and progress info */}
      <div style={{ fontSize: "9px", color: COLORS.GRAY6 }}>
        <div style={{ marginBottom: "2px" }}>
          <strong>Medium:</strong> {project.medium}
        </div>
        <div>
          <strong>Progress:</strong> {project.completionFraction}
          {/* Completion badge at bottom */}
          <SCompletionBadge isCompleted={project.isCompleted}>
            {project.isCompleted ? "✓ Done" : "In Progress"}
          </SCompletionBadge>
        </div>
      </div>
    </SProjectItem>
  )
}

const SemesterSection = ({
  semester,
  projects,
}: {
  semester: string
  projects: ProjectForDisplay[]
}) => (
  <AnimateInFlex col gap={16} animation={ANIMATION_BY_KEY.leftSm}>
    <h2
      style={{
        fontSize: "18px",
        fontWeight: "600",
        color: COLORS.GRAY8,
        margin: 0,
      }}
    >
      {formatSemester(semester)}
    </h2>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, 250px)",
        gap: "12px",
        justifyContent: "start",
      }}
    >
      {projects.map((project) => (
        <ProjectItem key={project.id} project={project} />
      ))}
    </div>
  </AnimateInFlex>
)

const StudentProjectsSection = ({
  projects,
}: {
  projects: Record<Uuid, number> | undefined
}) => {
  const projectsData = useProjectsData(projects)

  // Group by semester
  const groupedBySemester = useMemo(() => {
    const grouped: Record<string, ProjectForDisplay[]> = {}

    projectsData.forEach((project) => {
      if (!grouped[project.semester]) {
        grouped[project.semester] = []
      }
      grouped[project.semester].push(project)
    })

    return grouped
  }, [projectsData])

  const sortedSemesters = useMemo(() => {
    return sortBy(
      Object.keys(groupedBySemester),
      (semester) => semester
    ).reverse()
  }, [groupedBySemester])

  return (
    <Flex col gap={30} style={{ overflowY: "auto", paddingRight: 20 }}>
      {isEmpty(projectsData) && (
        <AnimateInFlex
          style={{ marginLeft: 20 }}
          animation={ANIMATION_BY_KEY.leftSm}
        >
          No projects found
        </AnimateInFlex>
      )}
      {sortedSemesters.map((semester) => (
        <SemesterSection
          key={semester}
          semester={semester}
          projects={groupedBySemester[semester]}
        />
      ))}
    </Flex>
  )
}

export default StudentProjectsSection
